import { useState } from 'react';
import { useChanges, useApproveChange, useRejectChange } from '@/api/supervisor';
import { useProfile } from '@/api/profile';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import {
  GitPullRequest,
  CheckCircle,
  XCircle,
  FileText,
  User,
  Clock,
  ExternalLink,
} from 'lucide-react';
import type { ChangeRequest } from '@/types';
import { canApproveChange } from './changes.helpers';
import { ChangeDecisionDialog } from './_components/change-decision-dialog';

type Decision = { change: ChangeRequest; mode: 'approve' | 'reject' };

const STATUS_BADGES: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  under_review: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pendiente',
  under_review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

export function ChangesPage() {
  // The numeric DB user id (for the canApprove gate) comes from /users/me — the
  // raw Auth0 user (useAuth) has no numeric id, only the `sub`.
  const { data: profile } = useProfile();
  const [tab, setTab] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [decision, setDecision] = useState<Decision | null>(null);
  const { data, isLoading, error } = useChanges({ status: tab, page, per_page: 20 });
  const approve = useApproveChange();
  const reject = useRejectChange();

  const changes = data?.data ?? [];
  const pagination = data?.meta?.pagination;

  const activeMut = decision?.mode === 'reject' ? reject : approve;

  function openDecision(change: ChangeRequest, mode: 'approve' | 'reject') {
    approve.reset();
    reject.reset();
    setDecision({ change, mode });
  }

  function handleConfirm(text: string) {
    if (!decision) return;
    const opts = { onSuccess: () => setDecision(null) };
    if (decision.mode === 'reject') {
      reject.mutate({ id: decision.change.id, reason: text }, opts);
    } else {
      approve.mutate({ id: decision.change.id, comments: text || undefined }, opts);
    }
  }

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="No se pudieron cargar las solicitudes de cambio." />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Aprobar ICNs</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Revisa y aprueba las solicitudes de cambio de documentos.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto rounded-lg bg-muted p-1">
        {[
          { key: undefined, label: 'Todos' },
          { key: 'pending', label: 'Pendientes' },
          { key: 'under_review', label: 'En revisión' },
          { key: 'approved', label: 'Aprobados' },
          { key: 'rejected', label: 'Rechazados' },
        ].map((t) => (
          <button
            key={t.key ?? 'all'}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={cn(
              'flex-1 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors',
              tab === t.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!changes.length ? (
        <EmptyState
          title="Sin solicitudes"
          description="No hay solicitudes de cambio en esta categoría."
        />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {changes.map((change) => (
              <ChangeCard
                key={change.id}
                change={change}
                canApprove={canApproveChange(change, profile?.id)}
                onApprove={() => openDecision(change, 'approve')}
                onReject={() => openDecision(change, 'reject')}
              />
            ))}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}

      {decision && (
        <ChangeDecisionDialog
          mode={decision.mode}
          changeTitle={decision.change.title}
          isPending={activeMut.isPending}
          error={activeMut.error}
          onConfirm={handleConfirm}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  );
}

function ChangeCard({
  change,
  canApprove,
  onApprove,
  onReject,
}: {
  change: ChangeRequest;
  canApprove: boolean;
  onApprove: () => void;
  onReject: () => void;
}) {
  const authorName = change.author ? `${change.author.first_name} ${change.author.last_name}` : '';
  const isActionable =
    (change.status === 'pending' || change.status === 'under_review') && canApprove;

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <GitPullRequest className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-card-foreground">{change.title}</h3>
          {change.document && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <FileText className="h-3 w-3" />
              <span>
                {change.document.code ? `${change.document.code} — ` : ''}
                {change.document.title}
              </span>
            </div>
          )}
          <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{change.description}</p>

          {change.external_url && (
            <a
              href={change.external_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <ExternalLink className="h-3 w-3" />
              Revisar documento (enlace externo)
            </a>
          )}

          <div className="mt-2 flex flex-wrap gap-1.5">
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-xs font-medium',
                STATUS_BADGES[change.status] ?? 'bg-gray-100 text-gray-600',
              )}
            >
              {STATUS_LABELS[change.status] ?? change.status}
            </span>
            {change.is_critical_change && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                Critico
              </span>
            )}
          </div>

          <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
            {authorName && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span>Solicitante: {authorName}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>Creada: {formatDate(change.created_at)}</span>
            </div>
          </div>

          {isActionable && (
            <div className="mt-3 flex gap-2">
              <button
                onClick={onApprove}
                className="flex items-center gap-1 rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
              >
                <CheckCircle className="h-3 w-3" /> Aprobar
              </button>
              <button
                onClick={onReject}
                className="flex items-center gap-1 rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
              >
                <XCircle className="h-3 w-3" /> Rechazar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
